<template>
  <div class="d-flex flex-column gap-4">
    <ErrorHandler
      v-if="serverError != null"
      :error="serverError"
      @done="serverError = null"
    />
    <div class="advance-reporting-date-filter-section">
      <div class="date-filter-right">
        <CustomSelectDatePicker
          placeholder="Select Date Range"
          class="date-filter-dropdown"
          v-model="localDateRange"
          :date-ranges="optionalDateRanges"
          :selected-school-year-limit="customSchoolYearLimit"
          use-selected-school-year-limit-end-date
          :limit-max-date="true"
          @update:model-value="onDateRangeChange"
        />
        <span class="date-range-text">{{ displayDateRange }}</span>
        <div class="divider"></div>
        <button
          type="button"
          class="widget-icon"
          aria-label="Personalize chart display"
          title="Personalize chart display"
          @click="openChartManagement"
          data-testid="widget-icon"
        >
          <i class="ri-function-line" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useGradeYears } from "@/composables/useGradeYears.js"
import advanceReportingConstants from "@/constants/advanceReportingConstants"
import {
  getMaxDate,
  getMinDate,
  getThisMonthRange
} from "@/helpers/advanceReportingDateUtils"
import ChartManagementPopUp from "@/v3components/AdvanceReporting/ChartManagementPopUp.vue"
import CustomSelectDatePicker from "@/v3components/shared/Form/CustomSelectDatePicker.vue"
import ErrorHandler from "@/v3components/VisitorErrorHandler.vue"
import moment from "moment-timezone"
import { computed, inject, nextTick, onMounted, ref } from "vue"
import { useStore } from "vuex"

function getPreviousDayRange(activeSchoolTimeZone) {
  const yesterday = moment().tz(activeSchoolTimeZone).subtract(1, "days")
  return {
    startDate: yesterday.format("MM/DD/YYYY"),
    endDate: yesterday.format("MM/DD/YYYY")
  }
}

function getThisWeekRange(minDate) {
  const weekStart = moment().startOf("week") // Sunday
  const weekEnd = moment().endOf("week") // Saturday
  const adjustedStart = weekStart.isBefore(minDate) ? minDate : weekStart

  return {
    startDate: adjustedStart.format("MM/DD/YYYY"),
    endDate: weekEnd.format("MM/DD/YYYY")
  }
}

function getThisYearRange(minDate, maxDate) {
  return {
    startDate: minDate.format("MM/DD/YYYY"),
    endDate: maxDate.format("MM/DD/YYYY")
  }
}

function getCustomDateRangeConstraints(minDate, maxDate) {
  const yesterday = moment().subtract(1, "days")
  const adjustedEndDate = yesterday.isBefore(maxDate) ? yesterday : maxDate
  return {
    minYear: minDate.year(),
    maxYear: adjustedEndDate.year(),
    startDate: minDate.format("MM/DD/YYYY"),
    endDate: adjustedEndDate.format("MM/DD/YYYY")
  }
}

export default {
  name: "DateFilter",
  components: {
    CustomSelectDatePicker,
    ErrorHandler
  },
  setup() {
    const store = useStore()
    const { currentSchoolYear } = useGradeYears()
    const modal = inject("modal")
    const cancelToken = inject("advanceReportingCancelToken", null)

    const serverError = ref(null)

    const selectedDateRange = computed(() => {
      return store?.getters?.["advanceReportingStore/selectedDateRange"] || null
    })

    const activeSchoolTimeZone = computed(() => {
      return store.getters["schools/activeSchoolTimeZone"]
    })

    const minDate = computed(() => {
      return getMinDate(
        currentSchoolYear.value,
        activeSchoolTimeZone.value,
        store.getters["schools/activeSchool"]
      )
    })

    const maxDate = computed(() => {
      return getMaxDate(currentSchoolYear.value, activeSchoolTimeZone.value)
    })

    const getDateRange = (rangeType) => {
      let range
      switch (rangeType) {
        case "previousDay":
          range = getPreviousDayRange(activeSchoolTimeZone.value)
          break
        case "thisWeek":
          range = getThisWeekRange(minDate.value)
          break
        case "thisMonth":
          range = getThisMonthRange(minDate.value)
          break
        case "thisYear":
          range = getThisYearRange(minDate.value, maxDate.value)
          break
        default:
          range = getThisMonthRange(minDate.value)
      }
      return range.startDate + " - " + range.endDate
    }

    const localDateRange = ref(null)

    const customSchoolYearLimit = computed(() => {
      const constraints = getCustomDateRangeConstraints(
        minDate.value,
        maxDate.value
      )
      return {
        value: [constraints.minYear, constraints.maxYear],
        endDate: constraints.endDate
      }
    })

    const optionalDateRanges = computed(() => {
      const customConstraints = getCustomDateRangeConstraints(
        minDate.value,
        maxDate.value
      )
      return [
        {
          id: advanceReportingConstants.DATE_RANGE_TYPES.YESTERDAY,
          label: "Previous day",
          range: getDateRange("previousDay")
        },
        {
          id: advanceReportingConstants.DATE_RANGE_TYPES.THIS_WEEK,
          label: "This week",
          range: getDateRange("thisWeek")
        },
        {
          id: advanceReportingConstants.DATE_RANGE_TYPES.THIS_MONTH,
          label: "This month",
          range: getDateRange("thisMonth")
        },
        {
          id: advanceReportingConstants.DATE_RANGE_TYPES.THIS_YEAR,
          label: "This year",
          range: getDateRange("thisYear")
        },
        {
          id: advanceReportingConstants.DATE_RANGE_TYPES.CUSTOM,
          label: "Custom range",
          range: null,
          startDate: customConstraints.startDate,
          endDate: customConstraints.endDate
        }
      ]
    })

    const displayDateRange = computed(() => {
      if (!localDateRange?.value) {
        return "Select date range"
      }

      const [startDate, endDate] = localDateRange?.value?.split(" - ") || [
        "",
        ""
      ]
      if (!startDate || !endDate) {
        return "Select date range"
      }

      const startDateObj = moment(startDate, "MM/DD/YYYY")
      const endDateObj = moment(endDate, "MM/DD/YYYY")

      const startDateFormatted = startDateObj.format("MMM D, YYYY")
      const endDateFormatted = endDateObj.format("MMM D, YYYY")

      return startDateFormatted + " - " + endDateFormatted
    })

    const calculatePreviousDates = (type, startDate, endDate) => {
      if (!startDate || !endDate) {
        return { previousStartDate: null, previousEndDate: null }
      }

      const currentStart = moment(startDate, "YYYY-MM-DD")

      let previousStartDate = null
      let previousEndDate = null

      switch (type) {
        case advanceReportingConstants.DATE_RANGE_TYPES.YESTERDAY: {
          const twoDaysAgo = currentStart.clone().subtract(1, "days")
          previousStartDate = twoDaysAgo.format("YYYY-MM-DD")
          previousEndDate = twoDaysAgo.format("YYYY-MM-DD")
          break
        }
        case advanceReportingConstants.DATE_RANGE_TYPES.THIS_WEEK: {
          const currentWeekStart = currentStart.clone().startOf("week") // Sunday
          const currentWeekEnd = currentStart.clone().endOf("week") // Saturday
          const previousWeekStart = currentWeekStart.clone().subtract(7, "days")
          const previousWeekEnd = currentWeekEnd.clone().subtract(7, "days")
          previousStartDate = previousWeekStart.format("YYYY-MM-DD")
          previousEndDate = previousWeekEnd.format("YYYY-MM-DD")
          break
        }
        case advanceReportingConstants.DATE_RANGE_TYPES.THIS_MONTH: {
          const currentMonthStart = currentStart.clone().startOf("month") // 1st day of the month
          const currentMonthEnd = currentStart.clone().endOf("month") // last day of the month
          const previousMonthStart = currentMonthStart
            .clone()
            .subtract(1, "month")
            .startOf("month")
          const previousMonthEnd = currentMonthEnd
            .clone()
            .subtract(1, "month")
            .endOf("month")
          previousStartDate = previousMonthStart.format("YYYY-MM-DD")
          previousEndDate = previousMonthEnd.format("YYYY-MM-DD")
          break
        }
        case advanceReportingConstants.DATE_RANGE_TYPES.THIS_YEAR: {
          const startYear = currentStart.year()
          const startMonth = currentStart.month() // 0-based: 0=Jan, 6=Jul
          const schoolYearStartYear =
            startMonth >= 6 ? startYear : startYear - 1
          const schoolYearEndYear = schoolYearStartYear + 1
          const currentSchoolYearStart = moment(`${schoolYearStartYear}-07-01`)
          const currentSchoolYearEnd = moment(`${schoolYearEndYear}-06-30`)
          const previousSchoolYearStart = currentSchoolYearStart
            .clone()
            .subtract(1, "year")
          const previousSchoolYearEnd = currentSchoolYearEnd
            .clone()
            .subtract(1, "year")
          previousStartDate = previousSchoolYearStart.format("YYYY-MM-DD")
          previousEndDate = previousSchoolYearEnd.format("YYYY-MM-DD")
          break
        }
        case advanceReportingConstants.DATE_RANGE_TYPES.CUSTOM:
        default: {
          previousStartDate = null
          previousEndDate = null
          break
        }
      }

      return { previousStartDate, previousEndDate }
    }

    const onDateRangeChange = async (newDateRange) => {
      if (!newDateRange) {
        return
      }

      if (newDateRange && newDateRange.includes(" - ")) {
        const [startDateStr, endDateStr] = newDateRange.split(" - ")
        const startDate = moment(startDateStr, "MM/DD/YYYY").format(
          "YYYY-MM-DD"
        )
        const endDate = moment(endDateStr, "MM/DD/YYYY").format("YYYY-MM-DD")

        const dateRangeType =
          optionalDateRanges?.value?.find(
            (range) => range?.range === newDateRange
          )?.id || advanceReportingConstants.DATE_RANGE_TYPES.CUSTOM

        const { previousStartDate, previousEndDate } = calculatePreviousDates(
          dateRangeType,
          startDate,
          endDate
        )

        updateStoreDateRange({
          type: dateRangeType,
          startDate,
          endDate,
          previousStartDate,
          previousEndDate
        })

        try {
          store.commit(
            "advanceReportingStore/setIsDateDependentChartDataLoading",
            true
          )
          await Promise.all([
            store.dispatch(
              "advanceReportingStore/fetchDateDependentChartData",
              {
                skipLoadingState: true,
                cancelToken
              }
            ),
            store.dispatch(
              "advanceReportingStore/fetchDateDependentLineChartData",
              {
                skipLoadingState: true,
                cancelToken
              }
            )
          ])
        } catch (error) {
          serverError.value = error
        } finally {
          store.commit(
            "advanceReportingStore/setIsDateDependentChartDataLoading",
            false
          )
        }
      }
    }

    const updateStoreDateRange = ({
      type,
      startDate,
      endDate,
      previousStartDate,
      previousEndDate
    }) => {
      store?.commit("advanceReportingStore/setSelectedDateRange", {
        type,
        startDate,
        endDate,
        previousStartDate,
        previousEndDate
      })
    }

    const openChartManagement = () => {
      modal.open(ChartManagementPopUp, {
        visible: true,
        size: "lg",
        title: "Personalize Chart Display",
        props: {}
      })
    }

    onMounted(async () => {
      await nextTick()

      const thisMonthDateRange =
        optionalDateRanges?.value?.find(
          (range) =>
            range?.id === advanceReportingConstants.DATE_RANGE_TYPES.THIS_MONTH
        ) || null
      if (!thisMonthDateRange || !thisMonthDateRange?.range) {
        return
      }

      localDateRange.value = thisMonthDateRange.range

      const [startDateStr, endDateStr] =
        thisMonthDateRange?.range?.split(" - ") || []
      if (!startDateStr || !endDateStr) {
        return
      }

      const startDate = moment(startDateStr, "MM/DD/YYYY").format("YYYY-MM-DD")
      const endDate = moment(endDateStr, "MM/DD/YYYY").format("YYYY-MM-DD")

      const { previousStartDate, previousEndDate } = calculatePreviousDates(
        thisMonthDateRange?.id ||
          advanceReportingConstants.DATE_RANGE_TYPES.THIS_MONTH,
        startDate,
        endDate
      )

      updateStoreDateRange({
        type:
          thisMonthDateRange?.id ||
          advanceReportingConstants.DATE_RANGE_TYPES.THIS_MONTH,
        startDate,
        endDate,
        previousStartDate,
        previousEndDate
      })
    })

    return {
      selectedDateRange,
      localDateRange,
      optionalDateRanges,
      displayDateRange,
      onDateRangeChange,
      customSchoolYearLimit,
      openChartManagement,
      serverError
    }
  }
}
</script>
